// No prototype reference — follows design system.
// Venues screen — FV-S1.
// List + detail in-screen replacement pattern (same as zones.tsx).
// Tabs: Zones | Operations | Settings.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Venue, VenueType, Zone, Operation, ZoneType } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import { useMockMode } from '@/shell/MockModeContext';
import {
  Btn, Badge, KLabel, Panel, Pill, Tabs,
  FONT_SIZE, FONT_WEIGHT, LETTER_SPACING, RADIUS, SIZE, SPACE,
  Icons, StatusDot, type ColorPalette,
} from '@vigilo/design-system';
import { getCrowdC, operationStatusColor } from '../../design/crowdPalette';
import { MOCK_OPERATIONS, MOCK_ZONES } from '../data/mock';
import { useVenues, type CreateVenueDto, type UpdateVenueDto, type VenueWithCounts } from '../hooks/useVenues';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
  useCrowdFilterActivity,
} from '../hooks/useCrowdActivityAudit';
import { useCrowdTaxonomy } from '../hooks/useCrowdTaxonomy';
import { useDensityData, useAlertData } from '../data/useMockData';
import { useLiveZoneMetrics, zoneMetricsDensitySnapshots } from '../hooks/useLiveZoneMetrics';
import { useZoneAlertSummaries } from '../hooks/useZoneAlertSummaries';
import { useOperationAlertSummaries } from '../hooks/useOperationAlertSummaries';
import { get } from '@vigilo/api-client';
import { useToast, ToastContainer } from '../components/Toast';
import { PaginationControls } from '../components/PaginationControls';
import { crowdQuery } from '../apiScope';
import { sortVenueOperationsForHistory } from '../utils/venue-operation-history';
import { defaultOperationEntryPath } from '../utils/operation-entry-routing';
import { resolveZoneStatus, zoneStatusColor, zoneStatusLabel, alertCountColor } from '../utils/zone-status';
import { useLocalPagination } from '../hooks/useLocalPagination';

// ── Constants ─────────────────────────────────────────────────────────────────

function fmtCapacity(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function shortId(id: string): string {
  return id.slice(0, 7);
}

// ── Zone helpers (for VenueZonesTab) ─────────────────────────────────────────

function fallbackZoneCode(zone: Pick<Zone, 'name'>): string {
  return zone.name.split(/\s+/).filter(Boolean).map(word => word[0]?.toUpperCase() ?? '').join('').slice(0, 5) || 'UNK';
}

function riskColor(risk: string, C: ColorPalette): string {
  if (risk === 'critical') return C.ember;
  if (risk === 'high')     return C.amber;
  if (risk === 'medium')   return C.sub;
  return C.muted;
}

function formatRelTime(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)    return 'just now';
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// ── Create venue modal ────────────────────────────────────────────────────────

function CreateVenueModal({ C, onClose, onCreate }: {
  C:        ColorPalette;
  onClose:  () => void;
  onCreate: (dto: CreateVenueDto) => Promise<void>;
}) {
  const { taxonomy, loading: taxonomyLoading } = useCrowdTaxonomy();
  const venueTypes = taxonomy?.venueTypes ?? [];
  const [name,     setName]     = useState('');
  const [vtype,    setVtype]    = useState<VenueType | ''>('');
  const [city,     setCity]     = useState('');
  const [country,  setCountry]  = useState('');
  const [address,  setAddress]  = useState('');
  const [capacity, setCapacity] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState<string | null>(null);

  const inputStyle = {
    background: C.surface, border: `1px solid ${C.border2}`, borderRadius: RADIUS.sm,
    padding: '8px 12px', fontSize: FONT_SIZE.body, color: C.text,
    fontFamily: "'Albert Sans', sans-serif", outline: 'none', width: '100%',
    boxSizing: 'border-box' as const,
  };

  async function handleSubmit() {
    if (!vtype) {
      setErr('Venue type is required.');
      return;
    }
    if (!name.trim() || !city.trim() || !country.trim() || !capacity) {
      setErr('Name, city, country and capacity are required.');
      return;
    }
    const cap = parseInt(capacity, 10);
    if (isNaN(cap) || cap < 1) { setErr('Capacity must be a positive number.'); return; }
    setSaving(true);
    setErr(null);
    try {
      await onCreate({
        name: name.trim(),
        venueType: vtype,
        city: city.trim(),
        country: country.trim().toUpperCase().slice(0, 2),
        address: address.trim() || undefined,
        totalCapacity: cap,
      });
      onClose();
    } catch {
      setErr('Failed to create venue — check Core API.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        background: C.surface, borderRadius: RADIUS.panel, border: `1px solid ${C.border2}`,
        width: 480, maxHeight: '90vh', overflow: 'auto',
        fontFamily: "'Albert Sans', sans-serif",
      }}>
        <div style={{
          padding: `${SPACE[16]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>
            Create Venue
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: `${SPACE[16]}px ${SPACE[18]}px`, display: 'flex', flexDirection: 'column', gap: SPACE[12] }}>
          <div>
            <KLabel C={C}>Venue Name</KLabel>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Johan Cruijff ArenA" autoFocus />
          </div>
          <div>
            <KLabel C={C}>Venue Type</KLabel>
            <select
              style={inputStyle}
              value={vtype}
              onChange={e => setVtype(e.target.value as VenueType)}
              disabled={taxonomyLoading || venueTypes.length === 0}
            >
              <option value="">{taxonomyLoading ? 'Loading venue types...' : 'Select venue type'}</option>
              {venueTypes.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[10] }}>
            <div>
              <KLabel C={C}>City</KLabel>
              <input style={inputStyle} value={city} onChange={e => setCity(e.target.value)} placeholder="Amsterdam" />
            </div>
            <div>
              <KLabel C={C}>Country (ISO 2)</KLabel>
              <input style={inputStyle} value={country} onChange={e => setCountry(e.target.value)} placeholder="NL" maxLength={2} />
            </div>
          </div>
          <div>
            <KLabel C={C}>Address</KLabel>
            <input style={inputStyle} value={address} onChange={e => setAddress(e.target.value)} placeholder="Street, postcode (optional)" />
          </div>
          <div>
            <KLabel C={C}>Total Capacity</KLabel>
            <input style={inputStyle} type="number" min={1} value={capacity} onChange={e => setCapacity(e.target.value)} placeholder="e.g. 54990" />
          </div>

          {err && (
            <div style={{
              background: C.emberDim, border: `1px solid ${C.ember}`, borderRadius: RADIUS.sm,
              padding: `${SPACE[10]}px ${SPACE[12]}px`, fontSize: FONT_SIZE.base, color: C.ember,
            }}>{err}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACE[8], paddingTop: SPACE[4] }}>
            <Btn C={C} variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn C={C} variant="primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Creating…' : 'Create Venue'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Venue list view ───────────────────────────────────────────────────────────

function VenueListView({ C, onVenueClick, canCreate, onCreate, venues, loading, error }: {
  C:            ColorPalette;
  onVenueClick: (v: VenueWithCounts) => void;
  canCreate:    boolean;
  onCreate:     () => void;
  venues:       VenueWithCounts[];
  loading:      boolean;
  error:        string | null;
}) {
  const { taxonomy, venueTypeLabel } = useCrowdTaxonomy();
  const venueTypes = taxonomy?.venueTypes ?? [];
  const [search,        setSearch]        = useState('');
  const [cityFilter,    setCityFilter]    = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [typeFilter,    setTypeFilter]    = useState<VenueType | ''>('');
  const [filter,        setFilter]        = useState<'all' | 'active' | 'archived'>('active');
  const hasSecondaryFilters = Boolean(search.trim() || cityFilter.trim() || countryFilter.trim() || typeFilter);
  useCrowdFilterActivity({
    page: '/venues',
    filterValues: {
      status: filter,
      city: cityFilter.trim() ? 'applied' : undefined,
      country: countryFilter.trim() ? 'applied' : undefined,
      venueType: typeFilter || undefined,
    },
    searchApplied: Boolean(search.trim()),
  });

  const filtered = venues
    .filter(v => {
      if (filter === 'active')   return v.archivedAt === null;
      if (filter === 'archived') return v.archivedAt !== null;
      return true;
    })
    .filter(v => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return [
        v.name,
        v.city,
        v.country,
        v.address ?? '',
        venueTypeLabel(v.venueType),
      ].some(value => value.toLowerCase().includes(query));
    })
    .filter(v => !cityFilter.trim() || v.city.toLowerCase().includes(cityFilter.trim().toLowerCase()))
    .filter(v => !countryFilter.trim() || v.country.toUpperCase().startsWith(countryFilter.trim().toUpperCase()))
    .filter(v => !typeFilter || v.venueType === typeFilter);
  const venuePagination = useLocalPagination(filtered);

  useEffect(() => {
    venuePagination.resetPage();
  }, [search, cityFilter, countryFilter, typeFilter, filter, venuePagination.resetPage]);

  const activeCount   = venues.filter(v => v.archivedAt === null).length;
  const archivedCount = venues.filter(v => v.archivedAt !== null).length;

  const tabList = [
    { id: 'active',   label: 'Active',   count: activeCount },
    { id: 'all',      label: 'All',      count: venues.length },
    { id: 'archived', label: 'Archived', count: archivedCount },
  ];

  const filterControlStyle = {
    background: C.surface, border: `1px solid ${C.border2}`, borderRadius: RADIUS.sm,
    padding: '6px 10px', fontSize: FONT_SIZE.base, color: C.text,
    fontFamily: "'Albert Sans', sans-serif", outline: 'none',
    boxSizing: 'border-box' as const, minWidth: 0,
  };

  function clearSecondaryFilters() {
    setSearch('');
    setCityFilter('');
    setCountryFilter('');
    setTypeFilter('');
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: SPACE[20], overflow: 'auto', minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACE[18] }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: FONT_SIZE.title, fontWeight: FONT_WEIGHT.bold, color: C.text }}>Venues</h1>
          <p style={{ margin: `${SPACE[4]}px 0 0`, fontSize: FONT_SIZE.base, color: C.muted }}>
            {activeCount} active venue{activeCount !== 1 ? 's' : ''}
          </p>
        </div>
        {canCreate && (
          <Btn C={C} variant="primary" icon={<Icons.Plus size={13} />} onClick={onCreate}>
            Add Venue
          </Btn>
        )}
      </div>

      {/* Tabs + filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[12], marginBottom: SPACE[10] }}>
        <div style={{ flex: 1 }}>
          <Tabs C={C} tabs={tabList} active={filter} onChange={v => setFilter(v as typeof filter)} />
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: SPACE[8],
        alignItems: 'center',
        marginBottom: SPACE[14],
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <Icons.Search size={12} />
          <input
            placeholder="Search venues…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...filterControlStyle, width: '100%' }}
          />
        </div>
        <input
          placeholder="City"
          value={cityFilter}
          onChange={e => setCityFilter(e.target.value)}
          style={filterControlStyle}
        />
        <input
          placeholder="Country"
          value={countryFilter}
          onChange={e => setCountryFilter(e.target.value.toUpperCase().slice(0, 2))}
          maxLength={2}
          style={filterControlStyle}
        />
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as VenueType | '')}
          style={filterControlStyle}
        >
          <option value="">All venue types</option>
          {venueTypes.map(type => (
            <option key={type.code} value={type.code}>{type.label}</option>
          ))}
        </select>
        <Btn
          C={C}
          variant="ghost"
          onClick={clearSecondaryFilters}
          disabled={!hasSecondaryFilters}
        >
          Clear
        </Btn>
      </div>

      {/* Table */}
      <Panel C={C}>
        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: C.muted, fontSize: FONT_SIZE.body }}>
            Loading venues…
          </div>
        ) : error ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: C.ember, fontSize: FONT_SIZE.body }}>
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: C.muted, fontSize: FONT_SIZE.body }}>
            {venues.length === 0 ? 'No venues configured.' : 'No venues match the current filters.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1.5px solid ${C.border2}` }}>
                {['Venue', 'Type', 'Location', 'Capacity', 'Active Zones', 'Active Ops', 'Status', 'Created', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
                    letterSpacing: LETTER_SPACING.wide, textTransform: 'uppercase', color: C.muted,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {venuePagination.pageRows.map(venue => {
                const isActive = venue.archivedAt === null;
                return (
                  <tr
                    key={venue.id}
                    style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', transition: 'background 100ms' }}
                    onMouseEnter={e => (e.currentTarget.style.background = C.faint)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    onClick={() => onVenueClick(venue)}
                  >
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontSize: FONT_SIZE.item, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>{venue.name}</div>
                      <div style={{ fontSize: FONT_SIZE.micro, color: C.muted, fontFamily: 'monospace', marginTop: 1 }}>
                        {shortId(venue.id)}
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <Badge C={C} color={C.sub} bg={C.faint} size={9}>
                        {venueTypeLabel(venue.venueType)}
                      </Badge>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: FONT_SIZE.body, color: C.sub }}>
                      {venue.city}, {venue.country}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: FONT_SIZE.body, color: C.text, fontWeight: FONT_WEIGHT.semibold, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtCapacity(venue.totalCapacity)}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: FONT_SIZE.body, color: C.text, fontWeight: FONT_WEIGHT.semibold, fontVariantNumeric: 'tabular-nums' }}>
                      {venue.activeZoneCount}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: FONT_SIZE.body, color: C.text, fontWeight: FONT_WEIGHT.semibold, fontVariantNumeric: 'tabular-nums' }}>
                      {venue.activeOperationCount}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <StatusDot color={isActive ? C.green : C.muted} size={5} />
                        <span style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: isActive ? C.green : C.muted, textTransform: 'uppercase', letterSpacing: LETTER_SPACING.wide }}>
                          {isActive ? 'Active' : 'Archived'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: FONT_SIZE.micro, color: C.muted }}>
                      {fmtDate(venue.createdAt)}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ fontSize: FONT_SIZE.base, color: C.amber }}><Icons.ChevR size={SIZE.iconSm} /></span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <PaginationControls
          C={C}
          pagination={venuePagination.pagination}
          loading={loading}
          error={error}
          emptyLabel={venues.length === 0 ? 'No venues configured.' : 'No venues match the current filters.'}
          onPageChange={venuePagination.setPage}
          onPageSizeChange={venuePagination.setPageSize}
        />
      </Panel>
    </div>
  );
}

// ── Venue detail — Zones tab ──────────────────────────────────────────────────

function VenueZonesTab({ C, venue, canCreate }: {
  C:         ColorPalette;
  venue:     Venue;
  canCreate: boolean;
}) {
  const navigate  = useNavigate();
  const { zoneTypeLabel, zoneTypeCode } = useCrowdTaxonomy();
  const { isMock } = useMockMode();
  const densityData = useDensityData();
  // A venue's zones are not scoped to a single Operation, so read live zone
  // metrics org-wide (operationId=null), same as the Admin > Zones catalog.
  const liveZoneMetrics = useLiveZoneMetrics(null, !isMock, 2000);
  const projectedSnapshots = useMemo(() => zoneMetricsDensitySnapshots(liveZoneMetrics.rows), [liveZoneMetrics.rows]);
  const snapshots = useMemo(
    () => isMock ? densityData.snapshots : { ...densityData.snapshots, ...projectedSnapshots },
    [densityData.snapshots, isMock, projectedSnapshots],
  );
  const [zones,   setZones]   = useState<Zone[]>(() =>
    isMock ? MOCK_ZONES.filter(z => z.venueId === venue.id) : []
  );
  const [loading, setLoading] = useState(!isMock);
  // Real active alert counts per zone (not the zone.status === 'alert' proxy).
  const zoneIds = useMemo(() => zones.map(z => z.id), [zones]);
  const zoneAlertSummaries = useZoneAlertSummaries(null, zoneIds, !isMock && zoneIds.length > 0, 'global');

  useEffect(() => {
    if (isMock) return;
    setLoading(true);
    get<Zone[] | { data?: Zone[] }>(`/venues/${venue.id}/zones?${crowdQuery({ archiveStatus: 'all' })}`)
      .then(body => setZones(Array.isArray(body) ? body : (body.data ?? [])))
      .catch(() => setZones([]))
      .finally(() => setLoading(false));
  }, [isMock, venue.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeZones = zones.filter(zone => !zone.archivedAt);
  const archivedZones = zones.filter(zone => !!zone.archivedAt);

  return (
    <div style={{ padding: `${SPACE[16]}px ${SPACE[18]}px` }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACE[14] }}>
        <span style={{ flex: 1, fontSize: FONT_SIZE.sub, color: C.muted }}>
          {activeZones.length} active zone{activeZones.length !== 1 ? 's' : ''} · {archivedZones.length} archived at this venue
        </span>
        {canCreate && (
          <Btn C={C} variant="ghost" icon={<Icons.Plus size={12} />}
            onClick={() => navigate(`/admin/zones?createForVenue=${venue.id}`)}>
            Create Zone
          </Btn>
        )}
      </div>

      {loading ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: C.muted, fontSize: FONT_SIZE.body }}>
          Loading zones…
        </div>
      ) : zones.length === 0 ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: C.muted, fontSize: FONT_SIZE.body }}>
          No zones linked to this venue yet.
        </div>
      ) : (
        <Panel C={C}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1.5px solid ${C.border2}` }}>
                {['Zone', 'Type', 'Status', 'Occupancy', 'Density', 'Risk', 'Alerts', 'Team', 'Last updated'].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold,
                    letterSpacing: LETTER_SPACING.wide, textTransform: 'uppercase', color: C.muted,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zones.map(zone => {
                const snap    = snapshots[zone.id];
                const util    = snap?.capacityUtilisation ?? 0;
                const isArchived = !!zone.archivedAt;
                const liveStatus = resolveZoneStatus(liveZoneMetrics.byZone[zone.id], snap);
                const stColor = isArchived ? C.muted : zoneStatusColor(liveStatus, C);
                const alertCount = isMock
                  ? (zone.status === 'alert' ? 1 : 0)
                  : (zoneAlertSummaries.summariesByZoneId.get(zone.id)?.active ?? 0);

                return (
                  <tr
                    key={zone.id}
                    style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', transition: 'background 100ms' }}
                    onMouseEnter={e => (e.currentTarget.style.background = C.faint)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    onClick={() => navigate('/admin/zones', { state: { openZone: zone } })}
                  >
                    {/* Zone name */}
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontSize: FONT_SIZE.item, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>{zone.name}</div>
                      <div style={{ fontSize: FONT_SIZE.micro, color: C.muted, fontFamily: 'monospace', marginTop: 1 }}>
                        {shortId(zone.id)} · {(zoneTypeCode(zone.type) === 'UNK' ? fallbackZoneCode(zone) : zoneTypeCode(zone.type))}
                      </div>
                    </td>

                    {/* Type badge */}
                    <td style={{ padding: '11px 14px' }}>
                      <Badge C={C} color={C.sub} bg={C.faint} size={9}>{zoneTypeLabel(zone.type)}</Badge>
                    </td>

                    {/* Status */}
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <StatusDot color={stColor} pulse={!isArchived && liveStatus === 'alert'} />
                        <span style={{ fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.semibold, color: stColor, textTransform: 'capitalize' }}>
                          {isArchived ? 'archived' : zoneStatusLabel(liveStatus).toLowerCase()}
                        </span>
                      </span>
                    </td>

                    {/* Occupancy */}
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontSize: FONT_SIZE.base, fontWeight: FONT_WEIGHT.bold, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
                        {!isArchived && snap ? snap.occupancyCount.toLocaleString() : '—'}
                      </div>
                      <div style={{ fontSize: FONT_SIZE.micro, color: C.muted }}>of {zone.capacity.toLocaleString()}</div>
                    </td>

                    {/* Density bar */}
                    <td style={{ padding: '11px 14px', minWidth: 100 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, height: SIZE.capacityBar, background: C.faint, borderRadius: RADIUS.sm, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', width: `${Math.min(util, 100)}%`,
                            background: stColor, transition: 'width 600ms ease',
                          }} />
                        </div>
                        <span style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: C.text, fontVariantNumeric: 'tabular-nums', minWidth: 30, textAlign: 'right' }}>
                          {util}%
                        </span>
                      </div>
                    </td>

                    {/* Risk level */}
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{
                        fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
                        textTransform: 'uppercase', letterSpacing: LETTER_SPACING.wide,
                        color: riskColor(zone.riskLevel, C),
                      }}>
                        {zone.riskLevel}
                      </span>
                    </td>

                    {/* Active alert count */}
                    <td style={{ padding: '11px 14px' }}>
                      {alertCount > 0 ? (
                        <span style={{
                          fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
                          color: alertCountColor(C), background: `${alertCountColor(C)}22`,
                          padding: `${SPACE[1]}px ${SPACE[6]}px`, borderRadius: RADIUS.tab,
                          fontVariantNumeric: 'tabular-nums',
                        }}>{alertCount}</span>
                      ) : (
                        <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>—</span>
                      )}
                    </td>

                    {/* Assigned team */}
                    <td style={{ padding: '11px 14px', fontSize: FONT_SIZE.body, color: C.sub }}>
                      {zone.assignedTeam ?? '—'}
                    </td>

                    {/* Last updated */}
                    <td style={{ padding: '11px 14px', fontSize: FONT_SIZE.micro, color: C.muted }}>
                      {formatRelTime(zone.updatedAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

// ── Operation helpers ─────────────────────────────────────────────────────────

function useElapsedMinutes(startIso: string | null): string {
  const [label, setLabel] = useState(() => {
    if (!startIso) return '';
    const mins = Math.floor((Date.now() - new Date(startIso).getTime()) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m elapsed` : `${m}m elapsed`;
  });

  useEffect(() => {
    if (!startIso) return;
    const tick = () => {
      const mins = Math.floor((Date.now() - new Date(startIso).getTime()) / 60000);
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      setLabel(h > 0 ? `${h}h ${m}m elapsed` : `${m}m elapsed`);
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [startIso]);

  return label;
}

function fmtScheduleTime(startIso: string, endIso: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const date = new Date(startIso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(startIso)} – ${fmt(endIso)} · ${date}`;
}

function fmtCountdown(scheduledStart: string): string {
  const diff = new Date(scheduledStart).getTime() - Date.now();
  if (diff <= 0) return 'Starting soon';
  const totalMins = Math.floor(diff / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return h > 0 ? `Starts in ${h}h ${m}m` : `Starts in ${m}m`;
}

function fmtOpDuration(actualStart: string | null, actualEnd: string | null): string {
  if (!actualStart || !actualEnd) return '';
  const mins = Math.floor((new Date(actualEnd).getTime() - new Date(actualStart).getTime()) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function opStatusAccent(status: string, C: ColorPalette): { bar: string; bg: string; text: string } {
  if (status === 'active')    return { bar: C.green,  bg: C.greenDim,  text: C.green };
  if (status === 'paused')    return { bar: C.amber,  bg: C.amberDim,  text: C.amber };
  if (status === 'scheduled') return { bar: C.teal,   bg: C.tealDim,   text: C.teal };
  if (status === 'closed')    return { bar: C.ember, bg: C.emberDim, text: C.ember };
  return { bar: C.muted, bg: C.faint, text: C.muted };
}

// ── OperationRow — separate component so useElapsedMinutes can be called per row

function OperationRow({ C, op, activeAlertCount, onMonitor, onViewSummary }: {
  C:                ColorPalette;
  op:               Operation;
  activeAlertCount: number;
  onMonitor:        () => void;
  onViewSummary:    () => void;
}) {
  const elapsed = useElapsedMinutes(op.actualStart);
  const accent  = opStatusAccent(op.status, C);
  const isActive    = op.status === 'active';
  const isPaused    = op.status === 'paused';
  const isScheduled = op.status === 'scheduled';
  const isClosed    = op.status === 'closed';
  const isLive      = isActive || isPaused;

  const eventTypeLabel = (op.eventType ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div style={{
      display: 'flex', borderRadius: RADIUS.card,
      background: C.panelHi, border: `1px solid ${C.border}`,
      overflow: 'hidden',
    }}>
      {/* Status accent bar */}
      <div style={{ width: 3, flexShrink: 0, background: accent.bar }} />

      <div style={{ flex: 1, padding: `${SPACE[12]}px ${SPACE[14]}px`, display: 'flex', alignItems: 'center', gap: SPACE[12] }}>
        {/* Main info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name + badges */}
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8], flexWrap: 'wrap', marginBottom: SPACE[6] }}>
            <span style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
              {op.name}
            </span>
            <span style={{
              fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
              color: accent.text, background: accent.bg,
              padding: `${SPACE[1]}px ${SPACE[6]}px`, borderRadius: RADIUS.tab,
              textTransform: 'uppercase', letterSpacing: LETTER_SPACING.wide,
            }}>
              {op.status}
            </span>
            {eventTypeLabel && (
              <Pill color={C.sub} hollow>
                {eventTypeLabel}
              </Pill>
            )}
          </div>

          {/* Metadata row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[14], flexWrap: 'wrap' }}>
            {/* Schedule */}
            <span style={{ display: 'flex', alignItems: 'center', gap: SPACE[4], fontSize: FONT_SIZE.xs, color: C.muted }}>
              <Icons.Clock size={SIZE.iconXs} />
              {fmtScheduleTime(op.scheduledStart, op.scheduledEnd)}
            </span>

            {/* Zones */}
            <span style={{ display: 'flex', alignItems: 'center', gap: SPACE[4], fontSize: FONT_SIZE.xs, color: C.muted }}>
              <Icons.Pin size={SIZE.iconXs} />
              {op.assignedZoneIds.length} zone{op.assignedZoneIds.length !== 1 ? 's' : ''}
            </span>

            {/* Elapsed / countdown / duration */}
            {isLive && elapsed && (
              <span style={{ fontSize: FONT_SIZE.xs, color: C.amber }}>{elapsed}</span>
            )}
            {isScheduled && (
              <span style={{ fontSize: FONT_SIZE.xs, color: C.teal }}>{fmtCountdown(op.scheduledStart)}</span>
            )}
            {isClosed && op.actualStart && op.actualEnd && (
              <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>
                {fmtOpDuration(op.actualStart, op.actualEnd)} duration
              </span>
            )}

            {/* Alerts */}
            {isLive && (
              <span style={{ display: 'flex', alignItems: 'center', gap: SPACE[4], fontSize: FONT_SIZE.xs, color: activeAlertCount > 0 ? alertCountColor(C) : C.muted }}>
                <Icons.Bell size={SIZE.iconXs} />
                {activeAlertCount} alert{activeAlertCount !== 1 ? 's' : ''}
              </span>
            )}
            {isClosed && (
              <span style={{ fontSize: FONT_SIZE.xs, color: C.muted }}>
                {activeAlertCount} total alert{activeAlertCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8], flexShrink: 0 }}>
          {isLive && (
            <Btn C={C} variant="primary" size="sm" onClick={onMonitor}>
              Monitor
            </Btn>
          )}
          {isClosed && (
            <Btn C={C} variant="ghost" size="sm" onClick={onViewSummary}
              style={{ color: C.muted, borderColor: C.border2 }}
            >
              View Summary
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Venue detail — Operations tab ─────────────────────────────────────────────

function VenueOperationsTab({ C, venue, canCreate, onNewOperation }: {
  C:              ColorPalette;
  venue:          Venue;
  canCreate:      boolean;
  onNewOperation: () => void;
}) {
  const navigate = useNavigate();
  const { currentUser, setSelectedOperation } = useApp();
  const { isMock } = useMockMode();
  const recordActivity = useCrowdActivityAudit();
  const { toasts, show: showToast, dismiss } = useToast();
  const alertData = useAlertData();

  const [ops,     setOps]     = useState<Operation[]>([]);
  const [loading, setLoading] = useState(true);

  const operationsPath = `/venues/${venue.id}/operations?${crowdQuery()}`;

  useEffect(() => {
    if (isMock) {
      setOps(MOCK_OPERATIONS.filter(op => op.venueId === venue.id));
      setLoading(false);
      return;
    }
    get<Operation[] | { data?: Operation[] }>(operationsPath)
      .then(res => {
        const list = (res as { data?: Operation[] }).data ?? (res as Operation[]);
        setOps(Array.isArray(list) ? list : []);
      })
      .catch(() => showToast('Failed to load operations.', 'error'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue.id, operationsPath, isMock]);

  // Real per-operation active-alert counts (same source as Operations Register).
  const opAlertSummaries = useOperationAlertSummaries(ops, !isMock);

  function alertCountForOp(op: Operation): number {
    if (isMock) {
      return alertData.alerts.filter(
        a => op.assignedZoneIds.includes(a.zoneId) && (a.status === 'open' || a.status === 'escalated'),
      ).length;
    }
    return opAlertSummaries.summariesByOperationId.get(op.id)?.active ?? 0;
  }

  function handleMonitor(op: Operation) {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.operationSelected,
      page: '/venues',
      operationId: op.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.operation,
      resourceId: op.id,
      metadata: { sourcePage: '/venues', targetPage: defaultOperationEntryPath(currentUser.role) },
    });
    setSelectedOperation(op);
    navigate(defaultOperationEntryPath(currentUser.role));
  }

  function handleViewSummary(op: Operation) {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: '/venues',
      operationId: op.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.operation,
      resourceId: op.id,
      metadata: { sourcePage: '/venues', targetPage: '/reports', templateId: 'operational_summary' },
    });
    setSelectedOperation(op);
    navigate(`/reports?template=operational_summary&operationId=${encodeURIComponent(op.id)}&from=${encodeURIComponent(op.scheduledStart)}&to=${encodeURIComponent(op.scheduledEnd)}`);
  }

  const sorted = sortVenueOperationsForHistory(ops);

  return (
    <div style={{ padding: `${SPACE[16]}px ${SPACE[18]}px` }}>
      {/* Tab header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACE[14] }}>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: SPACE[8] }}>
          <span style={{ fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
            Operation History
          </span>
          {!loading && (
            <span style={{
              fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
              color: C.muted, background: C.faint,
              padding: `${SPACE[1]}px ${SPACE[6]}px`, borderRadius: RADIUS.tab,
            }}>
              {ops.length}
            </span>
          )}
        </span>
        {canCreate && (
          <Btn C={C} variant="ghost" size="sm" icon={<Icons.Plus size={12} />} onClick={onNewOperation}>
            New Operation
          </Btn>
        )}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[6] }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              height: 72, borderRadius: RADIUS.card,
              background: C.panelHi, border: `1px solid ${C.border}`,
              opacity: 0.5,
            }} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && sorted.length === 0 && (
        <div style={{
          padding: `${SPACE[32]}px 0`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACE[12],
          textAlign: 'center',
        }}>
          <div style={{
            width: SIZE.avatarLg, height: SIZE.avatarLg, borderRadius: '50%',
            background: C.faint, border: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.muted,
          }}>
            <Icons.Shield size={SIZE.iconLg} />
          </div>
          <div>
            <div style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.semibold, color: C.text, marginBottom: SPACE[4] }}>
              No operations at this venue
            </div>
            <div style={{ fontSize: FONT_SIZE.base, color: C.muted }}>
              Schedule your first operation to begin crowd monitoring.
            </div>
          </div>
          {canCreate && (
            <Btn C={C} variant="ghost" size="sm"
              icon={<Icons.Plus size={12} />}
              onClick={onNewOperation}
              style={{ color: C.amber, borderColor: C.amber + '55' }}
            >
              Schedule First Operation
            </Btn>
          )}
        </div>
      )}

      {/* Operation rows */}
      {!loading && sorted.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[6] }}>
          {sorted.map(op => (
            <OperationRow
              key={op.id}
              C={C}
              op={op}
              activeAlertCount={alertCountForOp(op)}
              onMonitor={() => handleMonitor(op)}
              onViewSummary={() => handleViewSummary(op)}
            />
          ))}
        </div>
      )}

      <ToastContainer toasts={toasts} onDismiss={dismiss} C={C} />
    </div>
  );
}

// ── Venue detail — Settings tab ───────────────────────────────────────────────

function VenueSettingsTab({ C, venue, canEdit, canArchive, onUpdated, onArchived }: {
  C:          ColorPalette;
  venue:      Venue;
  canEdit:    boolean;
  canArchive: boolean;
  onUpdated:  (dto: UpdateVenueDto) => Promise<void>;
  onArchived: () => Promise<void>;
}) {
  const { taxonomy, loading: taxonomyLoading } = useCrowdTaxonomy();
  const venueTypes = taxonomy?.venueTypes ?? [];
  const [name,     setName]     = useState(venue.name);
  const [vtype,    setVtype]    = useState<VenueType>(venue.venueType);
  const [city,     setCity]     = useState(venue.city);
  const [country,  setCountry]  = useState(venue.country);
  const [address,  setAddress]  = useState(venue.address ?? '');
  const [capacity, setCapacity] = useState(String(venue.totalCapacity));
  const [saving,   setSaving]   = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [toast,    setToast]    = useState<string | null>(null);

  const inputStyle = {
    background: C.surface, border: `1px solid ${C.border2}`, borderRadius: RADIUS.sm,
    padding: '8px 12px', fontSize: FONT_SIZE.body, color: canEdit ? C.text : C.muted,
    fontFamily: "'Albert Sans', sans-serif", outline: 'none', width: '100%',
    boxSizing: 'border-box' as const,
  };

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSave() {
    const cap = parseInt(capacity, 10);
    if (!name.trim() || !city.trim() || !country.trim() || isNaN(cap) || cap < 1) {
      showToast('Please fill all required fields correctly.');
      return;
    }
    setSaving(true);
    try {
      await onUpdated({
        name: name.trim(), venueType: vtype,
        city: city.trim(), country: country.trim().toUpperCase().slice(0, 2),
        address: address.trim() || null, totalCapacity: cap,
      });
      showToast('Venue updated.');
    } catch {
      showToast('Update failed — check Core API.');
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    setArchiving(true);
    try {
      await onArchived();
    } catch {
      showToast('Archive blocked while active or paused operations use this venue.');
      setArchiving(false);
    }
  }

  return (
    <div style={{ padding: `${SPACE[16]}px ${SPACE[18]}px`, maxWidth: 480 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[12] }}>
        <div>
          <KLabel C={C}>Venue Name</KLabel>
          <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} disabled={!canEdit} />
        </div>
        <div>
          <KLabel C={C}>Venue Type</KLabel>
          <select style={inputStyle} value={vtype} onChange={e => setVtype(e.target.value as VenueType)} disabled={!canEdit || taxonomyLoading || venueTypes.length === 0}>
            {!venueTypes.some(t => t.code === vtype) && (
              <option value={vtype}>Unsupported Venue Type</option>
            )}
            {venueTypes.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[10] }}>
          <div>
            <KLabel C={C}>City</KLabel>
            <input style={inputStyle} value={city} onChange={e => setCity(e.target.value)} disabled={!canEdit} />
          </div>
          <div>
            <KLabel C={C}>Country (ISO 2)</KLabel>
            <input style={inputStyle} value={country} onChange={e => setCountry(e.target.value)} maxLength={2} disabled={!canEdit} />
          </div>
        </div>
        <div>
          <KLabel C={C}>Address</KLabel>
          <input style={inputStyle} value={address} onChange={e => setAddress(e.target.value)} disabled={!canEdit} />
        </div>
        <div>
          <KLabel C={C}>Total Capacity</KLabel>
          <input style={inputStyle} type="number" min={1} value={capacity} onChange={e => setCapacity(e.target.value)} disabled={!canEdit} />
        </div>

        {toast && (
          <div style={{
            background: C.amberDim, border: `1px solid ${C.amber}`, borderRadius: RADIUS.sm,
            padding: `${SPACE[8]}px ${SPACE[12]}px`, fontSize: FONT_SIZE.base, color: C.amber,
          }}>{toast}</div>
        )}

        {canEdit && (
          <div style={{ display: 'flex', gap: SPACE[8], paddingTop: SPACE[4] }}>
            <Btn C={C} variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Btn>
          </div>
        )}
      </div>

      {canArchive && venue.archivedAt === null && (
        <div style={{
          marginTop: SPACE[24],
          borderTop: `1px solid ${C.border}`,
          paddingTop: SPACE[16],
        }}>
          <KLabel C={C}>Danger Zone</KLabel>
          <p style={{ fontSize: FONT_SIZE.base, color: C.muted, margin: `0 0 ${SPACE[10]}px` }}>
            Archiving a venue removes it from active lists. Close active or paused operations at this venue first.
          </p>
          <Btn C={C} variant="ghost" onClick={handleArchive} disabled={archiving}
            style={{ color: C.ember, borderColor: C.ember + '55' }}>
            {archiving ? 'Archiving…' : 'Archive Venue'}
          </Btn>
        </div>
      )}
    </div>
  );
}

// ── Venue detail view ─────────────────────────────────────────────────────────

function VenueDetailView({ C, venue, onBack, canEdit, canArchive, canCreate, onUpdated, onArchived, onNewOperation, onTabChange }: {
  C:              ColorPalette;
  venue:          VenueWithCounts;
  onBack:         () => void;
  canEdit:        boolean;
  canArchive:     boolean;
  canCreate:      boolean;
  onUpdated:      (dto: UpdateVenueDto) => Promise<void>;
  onArchived:     () => Promise<void>;
  onNewOperation: () => void;
  onTabChange:    (tab: string) => void;
}) {
  const { venueTypeLabel } = useCrowdTaxonomy();
  const [tab, setTab] = useState('zones');
  const isActive = venue.archivedAt === null;

  const tabList = [
    { id: 'zones',      label: 'Zones' },
    { id: 'operations', label: 'Operations' },
    { id: 'settings',   label: 'Settings' },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}>
      {/* Back + header */}
      <div style={{ padding: `${SPACE[16]}px ${SPACE[20]}px`, borderBottom: `1px solid ${C.border}`, background: C.surface }}>
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', color: C.muted, cursor: 'pointer',
            fontSize: FONT_SIZE.base, display: 'flex', alignItems: 'center', gap: 6,
            padding: 0, marginBottom: SPACE[12], fontFamily: "'Albert Sans', sans-serif",
          }}
        >
          <Icons.ChevL size={SIZE.iconSm} /> Back to venues
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACE[16] }}>
          {/* Venue icon */}
          <div style={{
            width: SIZE.avatarLg, height: SIZE.avatarLg, borderRadius: RADIUS.card,
            background: C.amberDim, border: `1px solid ${C.amber}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icons.Map size={SIZE.iconLg} />
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[10], marginBottom: SPACE[4] }}>
              <h1 style={{ margin: 0, fontSize: FONT_SIZE.title, fontWeight: FONT_WEIGHT.bold, color: C.text }}>{venue.name}</h1>
              <Badge C={C} color={C.sub} bg={C.faint} size={9}>{venueTypeLabel(venue.venueType)}</Badge>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <StatusDot color={isActive ? C.green : C.muted} size={5} />
                <span style={{
                  fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
                  color: isActive ? C.green : C.muted,
                  textTransform: 'uppercase', letterSpacing: LETTER_SPACING.wide,
                }}>{isActive ? 'Active' : 'Archived'}</span>
              </div>
            </div>
            <div style={{ fontSize: FONT_SIZE.base, color: C.sub }}>
              {venue.address ? `${venue.address} · ` : ''}{venue.city}, {venue.country}
              {' · '}
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
                {venue.totalCapacity.toLocaleString()}
              </span> capacity
              {' · '}
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
                {venue.activeZoneCount}
              </span> active zone{venue.activeZoneCount !== 1 ? 's' : ''}
              {' · '}
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: FONT_WEIGHT.semibold, color: C.text }}>
                {venue.activeOperationCount}
              </span> active operation{venue.activeOperationCount !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div style={{ marginTop: SPACE[14] }}>
          <Tabs
            C={C}
            tabs={tabList}
            active={tab}
            onChange={next => {
              setTab(next);
              onTabChange(next);
            }}
          />
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {tab === 'zones'      && <VenueZonesTab      C={C} venue={venue} canCreate={canArchive} />}
        {tab === 'operations' && <VenueOperationsTab C={C} venue={venue} canCreate={canCreate} onNewOperation={onNewOperation} />}
        {tab === 'settings'   && (
          <VenueSettingsTab
            C={C} venue={venue}
            canEdit={canEdit} canArchive={canArchive}
            onUpdated={onUpdated} onArchived={onArchived}
          />
        )}
      </div>
    </div>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────

export function VenuesScreen() {
  const { theme, currentUser, openOperationModal } = useApp();
  const C = getCrowdC(theme);
  const recordActivity = useCrowdActivityAudit();

  const { venues, loading, error, createVenue, updateVenue, archiveVenue } = useVenues();
  const [view,          setView]          = useState<'list' | 'detail'>('list');
  const [selectedVenue, setSelectedVenue] = useState<VenueWithCounts | null>(null);
  const [showCreate,    setShowCreate]    = useState(false);

  const isAdmin      = currentUser.role === 'system_admin';
  const isSupervisor = currentUser.role === 'command_supervisor' || isAdmin;

  const handleVenueClick = useCallback((v: VenueWithCounts) => {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.recordViewed,
      page: '/venues',
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.venue,
      resourceId: v.id,
      metadata: { sourcePage: '/venues' },
    });
    setSelectedVenue(v);
    setView('detail');
  }, [recordActivity]);

  const handleBack = useCallback(() => {
    setView('list');
    setSelectedVenue(null);
  }, []);

  const handleUpdated = useCallback(async (dto: UpdateVenueDto) => {
    if (!selectedVenue) return;
    await updateVenue(selectedVenue.id, dto);
    setSelectedVenue(prev => prev ? { ...prev, ...dto, updatedAt: new Date().toISOString() } : prev);
  }, [selectedVenue, updateVenue]);

  const handleArchived = useCallback(async () => {
    if (!selectedVenue) return;
    await archiveVenue(selectedVenue.id);
    handleBack();
  }, [selectedVenue, archiveVenue, handleBack]);

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0, fontFamily: "'Albert Sans', sans-serif", background: C.bg }}>
      {view === 'list' && (
        <VenueListView
          C={C}
          onVenueClick={handleVenueClick}
          canCreate={isSupervisor}
          onCreate={() => setShowCreate(true)}
          venues={venues}
          loading={loading}
          error={error}
        />
      )}
      {view === 'detail' && selectedVenue && (
        <VenueDetailView
          C={C}
          venue={selectedVenue}
          onBack={handleBack}
          canEdit={isSupervisor}
          canArchive={isAdmin}
          canCreate={isSupervisor}
          onUpdated={handleUpdated}
          onArchived={handleArchived}
          onNewOperation={() => openOperationModal(selectedVenue.id)}
          onTabChange={next => recordActivity({
            action: CROWD_CLIENT_ACTIVITY_ACTION.tabChanged,
            page: '/venues',
            resourceType: CROWD_CLIENT_RESOURCE_TYPE.uiTab,
            resourceId: 'venue-detail',
            metadata: { tab: next, sourcePage: '/venues' },
          })}
        />
      )}
      {showCreate && (
        <CreateVenueModal
          C={C}
          onClose={() => setShowCreate(false)}
          onCreate={async dto => {
            await createVenue(dto);
            setSelectedVenue(null);
            setView('list');
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}
